import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import BackButton from '@lib/core/src/components/BackButton';
import Button from '@lib/core/src/components/Button';
import TextInput from '@lib/core/src/components/TextInput';
import { Stack } from '@mui/material';

import { useForgotPassword } from '@/api/generated';
import RoutesMap from '@/enums/RoutesMap';

import EmailSent from './EmailSent';
import {
  Container,
  InputAndTitleContainer,
  InputContainer,
  InputError,
  InputTitle,
  Title,
  TitleDescription,
  TitlesContainer
} from './ForgotPasswordStyles';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();

  const {
    mutateAsync: forgotPassword,
    isPending,
    isError: isPasswordResetError,
    isSuccess: emailSentSuccess
  } = useForgotPassword();

  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    if (emailSentSuccess) {
      setEmailSent(true);
    }
  }, [emailSentSuccess]);

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (value && !emailRegex.test(value)) {
      setEmailError('Invalid email address');
    } else {
      setEmailError('');
    }
  }, []);

  const isValidForm = !emailError && email;

  const handleResetPassword = useCallback(async () => {
    if (isValidForm) {
      forgotPassword({ data: { email } });
    }
  }, [email, forgotPassword, isValidForm]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleResetPassword();
  };

  if (emailSent) {
    return <EmailSent email={email} goBack={() => setEmailSent(false)} />;
  }

  return (
    <Container>
      <TitlesContainer>
        <Title>Forgot your password?</Title>
        <TitleDescription>
          Don't worry. Resetting your password is easy, just tell us the email address you
          registered with The Journey.
        </TitleDescription>
      </TitlesContainer>
      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
      >
        <InputTitle>Email</InputTitle>

        <Stack>
          <InputContainer>
            <TextInput
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={handleEmailChange}
            />
          </InputContainer>
          {emailError && <InputError>{emailError}</InputError>}
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap="16px">
          <BackButton onClick={() => navigate(RoutesMap.Login)} />
          <Button
            text="Reset my password"
            type="submit"
            loading={isPending}
            disabled={!isValidForm}
            onClick={handleSubmit}
          />
        </Stack>
      </form>
      <Stack>
        {isPasswordResetError && (
          <InputError>
            Oops.. There was an error reseting your password, please try again or contact support.
          </InputError>
        )}
      </Stack>
    </Container>
  );
};
export default ForgotPassword;
